import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { cancelReceipt, getMembershipReceipt } from "../../../../../apis/api";
import { Dialog } from "primereact/dialog";
import { RePrintInvoice } from "./RePrintInvoice";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import moment from "moment";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";

export const MembershipReceipts = () => {
    const userType = window.localStorage.getItem("userType");
    const invoiceRef = useRef(null);
    const toast = useRef(null);

    const handleMemberPrint = useReactToPrint({
        content: () => invoiceRef.current,
    });

    const [data, setData] = useState([]);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [singleReceipt, setSingleReceipt] = useState({});
    const [displayBasic2, setDisplayBasic2] = useState(false);
    const [displayFilterDialog, setDisplayFilterDialog] = useState(false);
    const [update, setUpdate] = useState(false);
    const [loading, setLoading] = useState(false);

    // Filter states
    const [tempFilters, setTempFilters] = useState({
        startDate: null,
        endDate: null,
        status: null,
        receiptNo: "",
        companyName: "",
        membershipCategory: "",
        paymentType: "",
    });

    const [appliedFilters, setAppliedFilters] = useState({
        startDate: null,
        endDate: null,
        status: null,
        receiptNo: "",
        companyName: "",
        membershipCategory: "",
        paymentType: "",
    });

    // Filter options
    const statusOptions = [
        { label: "All", value: null },
        { label: "Active", value: "active" },
        { label: "Cancelled", value: "cancelled" },
    ];

    const membershipCategoryOptions = [
        { label: "All", value: "" },
        { label: "Small Scale Service Activity", value: "ss" },
        { label: "Large Scale Service Activity", value: "sl" },
        { label: "Small Manufacturing", value: "ms" },
        { label: "Large Manufacturing", value: "ml" },
        { label: "Small Trading", value: "ts" },
        { label: "Large Trading", value: "tl" },
        { label: "District Chambers", value: "dc" },
        { label: "Association", value: "as" },
        { label: "Profession", value: "pr" },
    ];

    const paymentTypeOptions = [
        { label: "All", value: "" },
        { label: "Cheque", value: "Cheque" },
        { label: "NEFT", value: "NEFT" },
    ];

    const memberCategoryOptions = [
        { label: "All", value: "" },
        { label: "Ordinary", value: "Ordinary" },
        { label: "Patron", value: "Patron" },
    ];

    // Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true);

        const params = {
            pageNumber: Math.floor(first / rows) + 1,
            pageSize: rows,
        };

        // Apply filters
        if (appliedFilters.startDate)
            params.startDate = moment(appliedFilters.startDate).format("YYYY-MM-DD");
        if (appliedFilters.endDate)
            params.endDate = moment(appliedFilters.endDate).format("YYYY-MM-DD");
        if (appliedFilters.companyName)
            params.companyName = appliedFilters.companyName;
        if (appliedFilters.receiptNo)
            params.receiptNo = appliedFilters.receiptNo;
        if (appliedFilters.membershipCategory)
            params.membershipCategory = appliedFilters.membershipCategory;
        if (appliedFilters.paymentType)
            params.paymentType = appliedFilters.paymentType;

        // Handle status filter
        if (appliedFilters.status === 'cancelled') {
            params.cancelStatus = 'true';
        } else if (appliedFilters.status === 'active') {
            params.cancelStatus = 'false';
        }

        try {
            const res = await getMembershipReceipt(params);
            
            if (res && res.success) {
                setData(res.data.results || []);
                setTotalRecords(res.data.total || 0);
            } else {
                setData([]);
                setTotalRecords(0);
            }
        } catch (err) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch membership receipts",
            });
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [first, rows, appliedFilters, update]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Pagination
    const onPageChange = (e) => {
        setFirst(e.first);
        setRows(e.rows);
    };

    // Filter functions
    const clearFilters = () => {
        const clearedFilters = {
            startDate: null,
            endDate: null,
            status: null,
            receiptNo: "",
            companyName: "",
            membershipCategory: "",
            paymentType: "",
        };
        setTempFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setFirst(0);
        setDisplayFilterDialog(false);
    };

    const applyFilters = () => {
        setAppliedFilters(tempFilters);
        setFirst(0);
        setDisplayFilterDialog(false);
    };

    const openFilterDialog = () => {
        setTempFilters({ ...appliedFilters });
        setDisplayFilterDialog(true);
    };

    // Export to Excel
    const exportToExcel = () => {
        if (data.length === 0) {
            toast.current.show({
                severity: "warn",
                summary: "No Data",
                detail: "No data to export",
            });
            return;
        }

        const exportData = data.map((item, index) => ({
            "S.No": index + 1,
            "Receipt No": item.receipt_no || "",
            "Organization": item.name_of_org || "",
            "Member ID": item.MEM_id_no || "",
            "Member Name": item.first_name || "",
            "Membership Category": item.membership_category || "",
            "Member Category": item.member_category || "",
            "Payment Date": moment(item.date_of_payment).format("DD-MM-YYYY"),
            "Payment Type": item.payment_type || "",
            "Cheque No": item.cheque_number || "",
            "Status": item.cancel_status ? "Cancelled" : "Active",
            "Subscription Amount": item.subscription_amount || 0,
            "Admission Amount": item.admission_amount || 0,
            "Total Amount": item.paid_amount || 0,
            "City": item.city || "",
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Membership Receipts");
        
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { 
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
        });
        
        saveAs(dataBlob, `Membership_Receipts_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`);
    };

    // Table column renderers
    const receiptNo = (row) => (
        <span
            style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline" }}
            onClick={() => {
                setSingleReceipt(row);
                setDisplayBasic2(true);
            }}
        >
            {row.receipt_no}
        </span>
    );

    const getStatus = (row) => (
        <Tag 
            severity={row.cancel_status ? "danger" : "success"} 
            value={row.cancel_status ? "Cancelled" : "Active"} 
        />
    );

    const paymentDate = (row) => (
        moment(row.date_of_payment).format("DD-MM-YYYY")
    );

    const paymentInfo = (row) => (
        row.payment_type === 'cheque' && row.cheque_number 
            ? `${row.payment_type} (${row.cheque_number})`
            : row.payment_type || ''
    );

    const totalAmount = (row) => (
        `₹${(parseFloat(row.paid_amount) || 0).toLocaleString('en-IN')}`
    );

    // Cancel receipt
    const cancelMembershipReceipt = async (receipt) => {
        try {
            await cancelReceipt(receipt.member_id || receipt.id);
            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Receipt cancelled successfully",
            });
            setDisplayBasic2(false);
            setUpdate(prev => !prev);
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to cancel receipt",
            });
        }
    };

    // Filter dialog
    const renderFilterDialog = () => (
        <Dialog
            header="Filter Membership Receipts"
            visible={displayFilterDialog}
            style={{ width: "500px" }}
            onHide={() => setDisplayFilterDialog(false)}
        >
            <div className="p-fluid grid">
                <div className="field col-12 md:col-6">
                    <label>Start Date</label>
                    <Calendar
                        value={tempFilters.startDate}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, startDate: e.value }))}
                        dateFormat="dd/mm/yy"
                        showIcon
                    />
                </div>

                <div className="field col-12 md:col-6">
                    <label>End Date</label>
                    <Calendar
                        value={tempFilters.endDate}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, endDate: e.value }))}
                        dateFormat="dd/mm/yy"
                        showIcon
                    />
                </div>

                <div className="field col-12">
                    <label>Status</label>
                    <Dropdown
                        value={tempFilters.status}
                        options={statusOptions}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, status: e.value }))}
                        placeholder="Select Status"
                    />
                </div>

                <div className="field col-12">
                    <label>Membership Category</label>
                    <Dropdown
                        value={tempFilters.membershipCategory}
                        options={membershipCategoryOptions}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, membershipCategory: e.value }))}
                        placeholder="Select Category"
                    />
                </div>

                <div className="field col-12">
                    <label>Payment Type</label>
                    <Dropdown
                        value={tempFilters.paymentType}
                        options={paymentTypeOptions}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, paymentType: e.value }))}
                        placeholder="Select Payment Type"
                    />
                </div>

                {/* <div className="field col-12">
                    <label>Receipt No</label>
                    <InputText
                        value={tempFilters.receiptNo}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, receiptNo: e.target.value }))}
                        placeholder="Enter receipt number"
                    />
                </div> */}

                <div className="field col-12">
                    <label>Company Name</label>
                    <InputText
                        value={tempFilters.companyName}
                        onChange={(e) => setTempFilters(prev => ({ ...prev, companyName: e.target.value }))}
                        placeholder="Enter company name"
                    />
                </div>
            </div>

            <div className="flex justify-content-end gap-2 mt-4">
                <Button
                    label="Clear All"
                    icon="pi pi-trash"
                    className="p-button-outlined p-button-secondary"
                    onClick={clearFilters}
                />
                <Button
                    label="Apply Filters"
                    icon="pi pi-check"
                    onClick={applyFilters}
                />
            </div>
        </Dialog>
    );

    return (
        <div className="m-3">
            <Toast ref={toast} />
            {renderFilterDialog()}
            
            {loading && (
                <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex align-items-center justify-content-center z-5">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: '#000000ff' }} />
                </div>
            )}
            
            <Card className="shadow-1">
                <div className="flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="m-0">Membership Receipts</h3>
                        <p className="text-color-secondary m-0 mt-1">
                            Showing {first + 1} to {Math.min(first + rows, totalRecords)} of {totalRecords} records
                        </p>
                    </div>
                    
                    <div className="flex gap-2">
                        <Button
                            label="Filter"
                            icon="pi pi-filter"
                            className="p-button-outlined"
                            onClick={openFilterDialog}
                        />
                        <Button
                            label="Export"
                            icon="pi pi-file-excel"
                            className="p-button-outlined p-button-success"
                            onClick={exportToExcel}
                        />
                    </div>
                </div>

                <DataTable
                    value={data}
                    loading={loading}
                    size="small"
                    emptyMessage="No membership receipts found"
                    className="p-datatable-sm"
                >
                    <Column field="receipt_no" header="Receipt No" body={receiptNo} />
                    <Column field="name_of_org" header="Organization" />
                    <Column field="MEM_id_no" header="Member ID" />
                    <Column 
                        header="Member Name" 
                        body={(row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}
                    />
                    <Column header="Payment Date" body={paymentDate} />
                    <Column header="Payment Info" body={paymentInfo} />
                    <Column header="Status" body={getStatus} />
                    <Column header="Amount" body={totalAmount} />
                </DataTable>

                <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={onPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    className="mt-3 border-top-1"
                />
            </Card>

            <Dialog
                header="Receipt Details"
                visible={displayBasic2}
                style={{ width: "70vw" }}
                onHide={() => setDisplayBasic2(false)}
                footer={
                    <div>
                        <Button
                            label="Reprint"
                            icon="pi pi-print"
                            onClick={handleMemberPrint}
                            className="mr-2"
                        />
                        {/* {userType === "Account Team" && !singleReceipt.cancel_status && (
                            <Button
                                label="Cancel Receipt"
                                icon="pi pi-times"
                                className="p-button-danger"
                                onClick={() => cancelMembershipReceipt(singleReceipt)}
                            />
                        )} */}
                    </div>
                }
            >
                <div ref={invoiceRef}>
                    <RePrintInvoice data={singleReceipt} isDuplicate={false} />
                    <RePrintInvoice data={singleReceipt} isDuplicate={true} />
                </div>
            </Dialog>
        </div>
    );
};